<template>
	<BaseForm class="edit-job-queue-form" :verb="verb" v-bind="forward" @submit.prevent="onSubmit" :disable-submit="invalidResponseWindow">
		<p v-if="eventStartTimeHasPassed">The start time for this event has already passed.</p>
		<template v-else>
			<h3 class="fs-22 lh-26 mb-4">
				{{ inputModel.type == 'ranked' ? 'Confirm order of candidates' : 'Confirm your selected candidates' }}
			</h3>
			<p class="fs-16 lh-24 my-0">
				{{
					inputModel.type == 'ranked'
						? 'Drag and drop to create your offer. ShootWith.Me will use this order to contact your choices.'
						: 'Please confirm the job offer candidates in the list below. We will send a job offer to the entire list at the same time. The first person to accept the offer will be booked for the job.'
				}}
			</p>
			
			<JobQueue v-model="model" :can-remove-candidates="canRemoveCandidates" edit-mode @remove="$emit('remove', $event)" :class="inputModel.type" :queue-type="inputModel.type" context="findHelpQueue">
			</JobQueue>
			
			<div class="border flex-split wrap align-start gap-24 py-32 px-24 mt-16">
				<div class="mw-420">
					<h4 class="fs-16 lh-18 fw-700 mb-8">
						Time Window for Response {{ inputModel.type == 'ranked' ? '(Per Candidate)' : '' }}
					</h4>
					<p class="fs-14 lh-22 my-0" v-if="inputModel.type == 'ranked'">
						The amount of time each candidate will have to either accept or decline your job offer. Once this time expires, the next person in your queue will be offered the job, and the current person will no longer be able to accept it.
					</p>
					<p v-else>
						The amount of time all candidates will have to either accept or decline your job offer. Once this time expires, the job offer will end and candidates will no longer be able to accept this job. We will notify you that the job offer has expired, and you will be able to add new candidates and resend the job offer.
					</p>
				</div>
				<div class="flex-col gap-8 align-center text-center">
					<div class="fs-10 lh-14 caps fw-700 color-violet" style="letter-spacing:.6px;">Time Window</div>
					<div class="flex gap-4">
						<div class="response-window-input">
							<input v-model="responseWindowDays" id="days" type="tel" class="abs-cover" :disabled="isExistingQueue">
							<label for="days"></label>
							<span class="text">Days</span>
						</div>
						<div class="response-window-input">
							<input v-model="responseWindowHours" id="hours" type="tel" class="abs-cover" :disabled="isExistingQueue">
							<label for="hours"></label>
							<span class="text">Hours</span>
						</div>
					</div>
					<em v-if="!isExistingQueue" class="block fs-12 lh-16">Click to edit values</em>
				</div>
			</div>

			<!-- Time window errors -->
			<div v-if="successMessage.length"></div>
			<div v-else-if="responseWindowTooLong" class="error-message mt-16">
				Time window is too long.
			</div>
			<div v-else-if="responseWindowTooShort" class="error-message mt-16">
				Time window must be at least {{ this.queueModel.type == 'ranked' || eventIsTomorrow ? '1 hour' : '1 day'}}.
			</div>

		</template>
		<template v-if="!eventStartTimeHasPassed" #actions>
			<slot name="actions" />
		</template>
		<template v-if="!eventStartTimeHasPassed" #footer>
			<slot name="footer" />
		</template>
	</BaseForm>
</template>

<script>
import { JobQueue as JobQueueModel } from '@/models/Job';
import JobEvent from '@/models/JobEvent';
import objectForm from '@/mixins/objectForm';
import { millisecondsPer } from '@/plugins/time';
import JobQueue from '@/components/jobs/JobQueue';
import { DateTime } from 'luxon';

export default {
	name: 'EditJobQueueForm',
	mixins: [objectForm(JobQueueModel)],
	components: {
		JobQueue
	},
	props: {
		canRemoveCandidates: {
			type: Boolean,
			default: true,
		},
		copyModel: {
			type: Boolean,
			default: true,
		},
		event: {
			type: Object,
		},
		verb: {
			default: 'Save Queue',
		},
	},
	data() {
		return {
			responseWindowDays: 2,
			responseWindowHours: 0,
		};
	},
	computed: {
		eventIsTomorrow() {
			return this.eventStartTime < DateTime.now().plus({ hours: 24 }).ts;
		},
		responseWindow: {
			get() {
				let out = 0;
				if (this.responseWindowDays) {
					out += Number(this.responseWindowDays) * 1440;
				}

				if (this.responseWindowHours) {
					out += Number(this.responseWindowHours) * 60;
				}

				// Time in minutes
				return out;
			},
			set(minutes) {
				if (minutes < 1440 ) {
					this.responseWindowDays = 0;
					this.responseWindowHours = Math.floor(minutes / 60);
				} else {
					const totalHours = Math.floor((minutes / 60));
					const days = Math.floor(totalHours / 24);
					const remainderHours = totalHours - (days * 24);
					this.responseWindowDays = days;
					this.responseWindowHours = remainderHours;
				}
			}
		},
		responseWindowTooLong() {
			return this.responseWindow > this.maxResponseWindow;
		},
		responseWindowTooShort() {
			if (!this.queueModel) return false;

			if (this.queueModel.type == 'instant') {
				// If event is less tahn a day away, set to 1hr, otherwise set to 1 day
				const min = this.maxResponseWindow < 1440 ? 60 : (60 * 24);
				return this.responseWindow < min;
			} else {
				return this.responseWindow < 60;
			}
		},
		invalidResponseWindow() {
			return this.responseWindowTooLong || this.responseWindowTooShort;
		},
		eventId() {
			const queue = this.queueModel;
			if (!queue) return null;

			return queue.eventId || this.eventModel?.id || null;
		},
		eventModel() {
			const source = this.event ?? this.queueModel?.event;
			return source && typeof source === 'object' ? JobEvent.from(source) : null;
		},
		eventStartTime() {
			return this.getEventStartTime(this.eventModel);
		},
		eventStartTimeHasPassed() {
			const startTime = this.eventStartTime;
			return startTime != null && startTime <= DateTime.now().ts;
		},
		isExistingQueue() {
			return !!this.queueModel?.id;
		},
		maxResponseWindow() {
			return this.getMaxResponseWindow(this.queueModel, this.eventModel);
		},
		queueModel() {
			return this.model;
		},
	},
	methods: {
		getEventStartTime(eventData) {
			if (!eventData || typeof eventData !== 'object') {
				return null;
			}
			const event = JobEvent.from(eventData);
			let eventStart = this.$toDateTime(event.startedAt, {
				zone: this.$toTimeZone(event.timeZone) || this.$localTimeZone,
			});
			if (!eventStart) {
				return null;
			}
			if (eventStart.hour === 0 && eventStart.minute === 0 && eventStart.second === 1) {
				eventStart = eventStart.endOf('day');
			}
			return eventStart.toMillis();
		},
		getFutureCandidates(queueData) {
			if (!queueData || typeof queueData !== 'object') {
				return [];
			}
			const queue = JobQueueModel.from(queueData);
			const { candidates } = queue;

			if (!queue.id) {
				// Queue has no ID, which means it hasn't started yet. So all of the candidates are
				// considered future candidates, because none of them have been offered the job yet
				return candidates;
			}
			const { currentIndex } = queue;
			const futureCandidates = [];

			for (const candidate of candidates) {
				if (
					(currentIndex == null || candidate.order > currentIndex) &&
					candidate.status === 'inQueue' &&
					candidate.userId
				) {
					futureCandidates.push(candidate);
				}
			}
			return futureCandidates;
		},
		getMaxResponseWindow(queueData, eventData) {
			if (!queueData || typeof queueData !== 'object') {
				return null;
			}

			const queue = JobQueueModel.from(queueData);
			const millisecondsUntilEvent = this.getMillisecondsUntilEvent(eventData || queue.event);

			if (this.isExistingQueue) {
				return queue.responseWindow;
			}

			if (millisecondsUntilEvent == null) {
				return null;
			}

			if (millisecondsUntilEvent <= 0) {
				return 0;
			}

			if (queue.type == 'instant') {
				return Math.floor(millisecondsUntilEvent / 60000);
			}

			let availableMilliseconds = millisecondsUntilEvent;
			if (queue) {
				const { currentIndex } = queue;
				if (currentIndex != null) {
					// If a candidate currently has an offer, increase the total available response time by
					// the amount of time that's passed since the offer was first made.
					const currentCandidate = queue.candidates.find((c) => c.order === currentIndex);
					if (currentCandidate) {
						switch (currentCandidate.status) {
							case 'awaitingChangesResponse':
							case 'awaitingResponse': {
								const offerMadeAt =
									this.$toTimestamp(currentCandidate.queueReachedAt) ??
									this.$toTimestamp(queue.lastProcessedAt);
								if (offerMadeAt != null) {
									const now = Date.now();
									if (offerMadeAt < now) {
										availableMilliseconds += now - offerMadeAt;
									}
								}
								break;
							}
						}
					}
				}

				const futureCandidateCount = this.getFutureCandidates(queue).length;
				if (futureCandidateCount >= 1) {
					// Divide available time evenly among the candidates in the queue that haven't received a
					// job offer yet
					availableMilliseconds = Math.floor(availableMilliseconds / futureCandidateCount);
				}
			}

			return Math.floor(availableMilliseconds / millisecondsPer.minute);
		},
		getMillisecondsUntilEvent(eventData) {
			const startTime = this.getEventStartTime(eventData);
			return startTime == null ? null : startTime - Date.now();
		},
		async onSubmit() {
			let queue = this.model;
			if (!queue) return;

			this.clearMessages();
			const { jobId } = queue;
			const candidates = this.getFutureCandidates(queue).map((c) => ({
				userId: c.userId
			}));

			if (this.isExistingQueue) {
				const payload = {
					futureCandidates: candidates,
					id: queue.id,
					responseWindow: this.responseWindow,
				};
				let response = await this.action('jobs/updateJobQueue', payload);
				if (!response) {
					response = await this.action('jobs/getJobQueue', jobId);
				}
			} else {
				const payload = {
					candidates,
					jobId,
					responseWindow: this.responseWindow,
					type: queue.type
				};
				queue = await this.action('jobs/createJobQueue', payload);
				this.successMessage = 'Queue updated.';
			}

			this.save(queue);
		},
	},
	watch: {
		invalidResponseWindow: {
			handler(invalidResponseWindow) {
				
				if (invalidResponseWindow == true) {
					this.$emit('disable-submit');
				} else {
					this.$emit('enable-submit');
				}
			},
			immediate: true,
		},
		responseWindowDays: {
			handler(input) {
				const matches = String(input).match(/\d{1,2}/);
				this.responseWindowDays = matches ? matches[0] : '';
			}
		},
		responseWindowHours: {
			handler(input) {
				const matches = String(input).match(/\d{1,2}/);
				
				if (matches) {
					// Remove leading zero
					let inputValue = parseInt(matches[0], 10);
					this.responseWindowHours = inputValue > 24 ? 24 : inputValue;
				} else {
					this.responseWindowHours = '';
				}
			}
		},
		model: {
			handler(queue) {
				if (!queue) return;
				const sevenDays = 60 * 24 * 7;
				const twoDays = 60 * 24 * 2;
				if (this.isExistingQueue) {
					this.responseWindow = queue.responseWindow;
				} else if (queue.type == 'instant') {
					this.responseWindow = sevenDays > this.maxResponseWindow ? this.maxResponseWindow - 30 : sevenDays;
					
				} else {
					this.responseWindow = twoDays > this.maxResponseWindow ? this.maxResponseWindow : twoDays;	
				}
			},
			immediate: true,
		},
	},
};
</script>
